import React, { useState, useEffect } from 'react';
import { HiOutlineDocumentText, HiOutlineUpload, HiCheck } from 'react-icons/hi';
import { programmeService } from '../../services/programmeService';
import './styles/base.css';

function ProgrammeManagement({ selectedEdition }) {
  const [programmeData, setProgrammeData] = useState({
    pdfUrl: '',
    pdfPath: '',
    buttonText: 'Voir le Programme',
    isVisible: true,
    flashInfoPdfUrl: '',
    flashInfoPdfPath: '',
    flashInfoButtonText: 'Flash Info',
    flashInfoIsVisible: true,
    // Add new PDF buttons
    pdf2Url: '',
    pdf2Path: '',
    pdf2ButtonText: 'Document 2',
    pdf2IsVisible: true,
    pdf3Url: '',
    pdf3Path: '',
    pdf3ButtonText: 'Document 3',
    pdf3IsVisible: true
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [originalData, setOriginalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDatabaseEmpty, setIsDatabaseEmpty] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (selectedEdition) {
      loadProgrammeData();
    }
  }, [selectedEdition]);

  useEffect(() => {
    if (originalData) {
      const hasChanges = JSON.stringify(originalData) !== JSON.stringify(programmeData);
      setHasUnsavedChanges(hasChanges);
    }
  }, [programmeData, originalData]);

  const loadProgrammeData = async () => {
    if (!selectedEdition) return;
    
    try {
      setLoading(true);
      console.log('Loading programme data for edition:', selectedEdition);
      const data = await programmeService.getProgrammeData(selectedEdition);
      
      if (!data) {
        // Database is empty, set default values
        console.log('No data found, setting defaults');
        setIsDatabaseEmpty(true);
        const defaultData = {
          pdfUrl: '',
          pdfPath: '',
          buttonText: 'Voir le Programme',
          isVisible: true,
          flashInfoPdfUrl: '',
          flashInfoPdfPath: '',
          flashInfoButtonText: 'Flash Info',
          flashInfoIsVisible: true,
          pdf2Url: '',
          pdf2Path: '',
          pdf2ButtonText: 'Document 2',
          pdf2IsVisible: true,
          pdf3Url: '',
          pdf3Path: '',
          pdf3ButtonText: 'Document 3',
          pdf3IsVisible: true
        };
        setProgrammeData(defaultData);
        setOriginalData(JSON.parse(JSON.stringify(defaultData)));
      } else {
        console.log('Data found:', data);
        setProgrammeData(data);
        setOriginalData(JSON.parse(JSON.stringify(data)));
        setIsDatabaseEmpty(false);
      }
    } catch (error) {
      console.error('Error loading programme data:', error);
      setIsDatabaseEmpty(true);
      // Set default data for editing
      const defaultData = {
        pdfUrl: '',
        pdfPath: '',
        buttonText: 'Voir le Programme',
        isVisible: true,
        flashInfoPdfUrl: '',
        flashInfoPdfPath: '',
        flashInfoButtonText: 'Flash Info',
        flashInfoIsVisible: true,
        pdf2Url: '',
        pdf2Path: '',
        pdf2ButtonText: 'Document 2',
        pdf2IsVisible: true,
        pdf3Url: '',
        pdf3Path: '',
        pdf3ButtonText: 'Document 3',
        pdf3IsVisible: true
      };
      setProgrammeData(defaultData);
      setOriginalData(JSON.parse(JSON.stringify(defaultData)));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await programmeService.updateProgrammeData(programmeData, selectedEdition);
      setOriginalData(JSON.parse(JSON.stringify(programmeData)));
      setHasUnsavedChanges(false);
      setIsDatabaseEmpty(false);
      setShowSaveSuccess(true);
      
      setTimeout(() => {
        setShowSaveSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error saving programme data:', error);
      alert('Erreur lors de la sauvegarde. Veuillez réessayer.');
    }
  };

  const handleReset = () => {
    if (originalData && window.confirm('Voulez-vous vraiment annuler toutes les modifications non sauvegardées ?')) {
      setProgrammeData(JSON.parse(JSON.stringify(originalData)));
      setHasUnsavedChanges(false);
    }
  };

  const handleInputChange = (field, value) => {
    setProgrammeData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      alert('Veuillez sélectionner un fichier PDF valide.');
      return;
    }

    // Validate file size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      alert('Le fichier est trop volumineux. Taille maximum: 50MB.');
      return;
    }

    try {
      setUploading(true);

      // Delete old PDF if exists
      if (programmeData.pdfPath) {
        await programmeService.deleteProgrammePDF(programmeData.pdfPath);
      }

      // Upload new PDF
      const uploadResult = await programmeService.uploadProgrammePDF(file, selectedEdition);
      
      // Update programme data with new PDF
      handleInputChange('pdfUrl', uploadResult.url);
      handleInputChange('pdfPath', uploadResult.path);

      alert('Fichier PDF uploadé avec succès !');
    } catch (error) {
      console.error('Error uploading PDF:', error);
      alert('Erreur lors du téléchargement du fichier. Veuillez réessayer.');
    } finally {
      setUploading(false);
    }
  };

  const handleFlashInfoFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      alert('Veuillez sélectionner un fichier PDF valide.');
      return;
    }

    // Validate file size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      alert('Le fichier est trop volumineux. Taille maximum: 50MB.');
      return;
    }

    try {
      setUploading(true);

      // Delete old PDF if exists
      if (programmeData.flashInfoPdfPath) {
        await programmeService.deleteFlashInfoPDF(programmeData.flashInfoPdfPath);
      }

      // Upload new PDF
      const uploadResult = await programmeService.uploadFlashInfoPDF(file, selectedEdition);
      
      // Update programme data with new PDF
      handleInputChange('flashInfoPdfUrl', uploadResult.url);
      handleInputChange('flashInfoPdfPath', uploadResult.path);

      alert('Fichier Flash Info PDF uploadé avec succès !');
    } catch (error) {
      console.error('Error uploading Flash Info PDF:', error);
      alert('Erreur lors du téléchargement du fichier. Veuillez réessayer.');
    } finally {
      setUploading(false);
    }
  };

  const handlePDF2FileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      alert('Veuillez sélectionner un fichier PDF valide.');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      alert('Le fichier est trop volumineux. Taille maximum: 50MB.');
      return;
    }

    try {
      setUploading(true);

      if (programmeData.pdf2Path) {
        await programmeService.deletePDF2(programmeData.pdf2Path);
      }

      const uploadResult = await programmeService.uploadPDF2(file, selectedEdition);
      
      handleInputChange('pdf2Url', uploadResult.url);
      handleInputChange('pdf2Path', uploadResult.path);

      alert('Document 2 PDF uploadé avec succès !');
    } catch (error) {
      console.error('Error uploading PDF2:', error);
      alert('Erreur lors du téléchargement du fichier. Veuillez réessayer.');
    } finally {
      setUploading(false);
    }
  };

  const handlePDF3FileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      alert('Veuillez sélectionner un fichier PDF valide.');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      alert('Le fichier est trop volumineux. Taille maximum: 50MB.');
      return;
    }

    try {
      setUploading(true);

      if (programmeData.pdf3Path) {
        await programmeService.deletePDF3(programmeData.pdf3Path);
      }

      const uploadResult = await programmeService.uploadPDF3(file, selectedEdition);
      
      handleInputChange('pdf3Url', uploadResult.url);
      handleInputChange('pdf3Path', uploadResult.path);

      alert('Document 3 PDF uploadé avec succès !');
    } catch (error) {
      console.error('Error uploading PDF3:', error);
      alert('Erreur lors du téléchargement du fichier. Veuillez réessayer.');
    } finally {
      setUploading(false);
    }
  };

  if (!selectedEdition) {
    return <div className="admin-section">Please select an edition first</div>;
  }

  if (loading) {
    return <div className="admin-section">Loading...</div>;
  }

  return (
    <div className="admin-section">
      {/* Database Empty Warning */}
      {isDatabaseEmpty && (
        <div className="database-empty-warning">
          <div className="warning-content">
            <h3>Base de données vide pour l'édition {selectedEdition}</h3>
            <p>La base de données Programme est vide pour cette édition. Remplissez les champs ci-dessous et cliquez sur "Sauvegarder" pour initialiser les données.</p>
          </div>
        </div>
      )}

      {/* Save/Reset Actions */}
      {hasUnsavedChanges && (
        <div className="save-actions-bar">
          <div className="save-message">
            <span>Vous avez des modifications non sauvegardées</span>
          </div>
          <div className="save-buttons">
            <button onClick={handleReset} className="btn-secondary">
              Annuler les modifications
            </button>
            <button onClick={handleSave} className="btn-primary save-btn">
              <HiCheck /> Sauvegarder
            </button>
          </div>
        </div>
      )}

      {/* Success Message */}
      {showSaveSuccess && (
        <div className="save-success-message">
          <HiCheck /> Modifications sauvegardées avec succès !
        </div>
      )}

      {/* Section Header */}
      <div className="section-header">
        <h2>Gestion des Boutons PDF</h2>
        <p>Gérez tous les boutons PDF avec leurs fichiers et paramètres de visibilité</p>
      </div>

      {/* Form Grid */}
      <div className="form-grid">
        {/* Programme Section */}
        <div className="form-section">
          <h3>Programme de Conférence</h3>
          
          <div className="form-group">
            <label>Texte du bouton programme</label>
            <input
              type="text"
              value={programmeData.buttonText}
              onChange={(e) => handleInputChange('buttonText', e.target.value)}
              placeholder="Texte affiché sur le bouton"
            />
          </div>

          <div className="form-group">
            <label>Visibilité du bouton programme</label>
            <div className="checkbox-group">
              <input
                type="checkbox"
                id="isVisible"
                checked={programmeData.isVisible}
                onChange={(e) => handleInputChange('isVisible', e.target.checked)}
              />
              <label htmlFor="isVisible">Afficher le bouton programme</label>
            </div>
          </div>

          <div className="form-group full-width">
            <label>Fichier PDF du programme</label>
            <div className="file-upload-section">
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileUpload}
                id="pdf-upload"
                style={{ display: 'none' }}
                disabled={uploading}
              />
              <div className="file-actions">
                <button
                  type="button"
                  className={`btn-secondary ${uploading ? 'uploading' : ''}`}
                  onClick={() => document.getElementById('pdf-upload').click()}
                  disabled={uploading}
                >
                  <HiOutlineUpload />
                  {uploading ? 'Téléchargement...' : 'Uploader un nouveau PDF'}
                </button>
              </div>
              {programmeData.pdfUrl && (
                <div className="current-file">
                  <HiOutlineDocumentText />
                  <span>Fichier actuel disponible</span>
                  <a 
                    href={programmeData.pdfUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="view-file-btn"
                  >
                    Voir le fichier
                  </a>
                </div>
              )}
              <p className="upload-hint">PDF uniquement (max. 50MB)</p>
            </div>
          </div>
        </div>

        {/* Flash Info Section */}
        <div className="form-section">
          <h3>Flash Info</h3>
          
          <div className="form-group">
            <label>Texte du bouton flash info</label>
            <input
              type="text"
              value={programmeData.flashInfoButtonText}
              onChange={(e) => handleInputChange('flashInfoButtonText', e.target.value)}
              placeholder="Texte affiché sur le bouton flash info"
            />
          </div>

          <div className="form-group">
            <label>Visibilité du bouton flash info</label>
            <div className="checkbox-group">
              <input
                type="checkbox"
                id="flashInfoIsVisible"
                checked={programmeData.flashInfoIsVisible}
                onChange={(e) => handleInputChange('flashInfoIsVisible', e.target.checked)}
              />
              <label htmlFor="flashInfoIsVisible">Afficher le bouton flash info</label>
            </div>
          </div>

          <div className="form-group full-width">
            <label>Fichier PDF du flash info</label>
            <div className="file-upload-section">
              <input
                type="file"
                accept=".pdf"
                onChange={handleFlashInfoFileUpload}
                id="flash-info-pdf-upload"
                style={{ display: 'none' }}
                disabled={uploading}
              />
              <div className="file-actions">
                <button
                  type="button"
                  className={`btn-secondary ${uploading ? 'uploading' : ''}`}
                  onClick={() => document.getElementById('flash-info-pdf-upload').click()}
                  disabled={uploading}
                >
                  <HiOutlineUpload />
                  {uploading ? 'Téléchargement...' : 'Uploader un nouveau PDF'}
                </button>
              </div>
              {programmeData.flashInfoPdfUrl && (
                <div className="current-file">
                  <HiOutlineDocumentText />
                  <span>Fichier actuel disponible</span>
                  <a 
                    href={programmeData.flashInfoPdfUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="view-file-btn"
                  >
                    Voir le fichier
                  </a>
                </div>
              )}
              <p className="upload-hint">PDF uniquement (max. 50MB)</p>
            </div>
          </div>
        </div>

        {/* Document 2 Section */}
        <div className="form-section">
          <h3>Document 2</h3>
          
          <div className="form-group">
            <label>Texte du bouton Document 2</label>
            <input
              type="text"
              value={programmeData.pdf2ButtonText}
              onChange={(e) => handleInputChange('pdf2ButtonText', e.target.value)}
              placeholder="Texte affiché sur le bouton"
            />
          </div>

          <div className="form-group">
            <label>Visibilité du bouton Document 2</label>
            <div className="checkbox-group">
              <input
                type="checkbox"
                id="pdf2IsVisible"
                checked={programmeData.pdf2IsVisible}
                onChange={(e) => handleInputChange('pdf2IsVisible', e.target.checked)}
              />
              <label htmlFor="pdf2IsVisible">Afficher le bouton Document 2</label>
            </div>
          </div>

          <div className="form-group full-width">
            <label>Fichier PDF Document 2</label>
            <div className="file-upload-section">
              <input
                type="file"
                accept=".pdf"
                onChange={handlePDF2FileUpload}
                id="pdf2-upload"
                style={{ display: 'none' }}
                disabled={uploading}
              />
              <div className="file-actions">
                <button
                  type="button"
                  className={`btn-secondary ${uploading ? 'uploading' : ''}`}
                  onClick={() => document.getElementById('pdf2-upload').click()}
                  disabled={uploading}
                >
                  <HiOutlineUpload />
                  {uploading ? 'Téléchargement...' : 'Uploader Document 2'}
                </button>
              </div>
              {programmeData.pdf2Url && (
                <div className="current-file">
                  <HiOutlineDocumentText />
                  <span>Fichier actuel disponible</span>
                  <a 
                    href={programmeData.pdf2Url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="view-file-btn"
                  >
                    Voir le fichier
                  </a>
                </div>
              )}
              <p className="upload-hint">PDF uniquement (max. 50MB)</p>
            </div>
          </div>
        </div>

        {/* Document 3 Section */}
        <div className="form-section">
          <h3>Document 3</h3>
          
          <div className="form-group">
            <label>Texte du bouton Document 3</label>
            <input
              type="text"
              value={programmeData.pdf3ButtonText}
              onChange={(e) => handleInputChange('pdf3ButtonText', e.target.value)}
              placeholder="Texte affiché sur le bouton"
            />
          </div>

          <div className="form-group">
            <label>Visibilité du bouton Document 3</label>
            <div className="checkbox-group">
              <input
                type="checkbox"
                id="pdf3IsVisible"
                checked={programmeData.pdf3IsVisible}
                onChange={(e) => handleInputChange('pdf3IsVisible', e.target.checked)}
              />
              <label htmlFor="pdf3IsVisible">Afficher le bouton Document 3</label>
            </div>
          </div>

          <div className="form-group full-width">
            <label>Fichier PDF Document 3</label>
            <div className="file-upload-section">
              <input
                type="file"
                accept=".pdf"
                onChange={handlePDF3FileUpload}
                id="pdf3-upload"
                style={{ display: 'none' }}
                disabled={uploading}
              />
              <div className="file-actions">
                <button
                  type="button"
                  className={`btn-secondary ${uploading ? 'uploading' : ''}`}
                  onClick={() => document.getElementById('pdf3-upload').click()}
                  disabled={uploading}
                >
                  <HiOutlineUpload />
                  {uploading ? 'Téléchargement...' : 'Uploader Document 3'}
                </button>
              </div>
              {programmeData.pdf3Url && (
                <div className="current-file">
                  <HiOutlineDocumentText />
                  <span>Fichier actuel disponible</span>
                  <a 
                    href={programmeData.pdf3Url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="view-file-btn"
                  >
                    Voir le fichier
                  </a>
                </div>
              )}
              <p className="upload-hint">PDF uniquement (max. 50MB)</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProgrammeManagement;
